import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { toast } from "react-toastify";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { DesktopHeader, MobileHeader } from "../Account/components";
import {
  employeeAccountPath,
  employeeDebitCardInfoPath,
} from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";
import {
  FieldHeader,
  SectionFlexColumn,
  SectionFlexRow,
  TextInput,
} from "../../../../components/General";
import Button from "../../../../components/Button";
import Loader from "../../../../components/Loader";
import ErrorPage from "../../../../components/ErrorPage";
import useGetMailingAddress from "../../../../hooks/queries/employee/useGetMailingAddress";
import useUpdateMailingAddress from "../../../../hooks/mutations/employee/useUpdateMailingAddress";
import { STATES } from "../../../../helpers/content";

const DebitCardChangeAddress = () => {
  const { data, status } = useGetMailingAddress();
  const { mutateAsync: updateMailingAddress, status: updateAddressStatus } =
    useUpdateMailingAddress();
  const [street, setStreet] = useState("");
  const [unit, setUnit] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const routeState = useLocation().state;
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const title = "Change Address";
  const subtitle = "Keep your profile up to date";
  const breadcrumbs = ["My Account", "Debit Card", title];

  useEffect(() => {
    if (data?.shipping_address) {
      const { shipping_address } = data;
      setStreet(shipping_address.street);
      setUnit(shipping_address.unit || "");
      setCity(shipping_address.city);
      setState(shipping_address.state);
      setZip(shipping_address.zip_code);
    }
  }, [data]);

  if (status === "loading" || updateAddressStatus === "loading")
    return <Loader />;
  if (status === "error") return <ErrorPage />;

  const doBack = () => {
    // Optional route prop to pass in to define which page to navigate back to
    let path = employeeDebitCardInfoPath;
    if (routeState?.navigateBackPath) {
      path = routeState.navigateBackPath;
    }

    navigate(path);
  };

  const validate = () => {
    if (!street || !city || !state || !zip) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    try {
      const isValid = validate();
      if (!isValid) {
        toast.error("Please fill out all fields");
        return;
      }
      const response = await updateMailingAddress({
        address: { street, city, state, unit, zip_code: zip },
      });

      if (response.shipping_address) {
        toast.success("Mailing address updated successfully!");
        doBack();
        return;
      }
    } catch (error) {
      toast.error("Unable to update mailing address");
    }
  };

  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        <SectionFlexColumn>
          <FieldHeader>Mailing Address</FieldHeader>
          <TextInput
            size="small"
            placeholder="Mailing address"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
          />
          <SectionFlexRow sx={{ gap: "16px" }}>
            <div className="flex-1">
              <FieldHeader>Unit</FieldHeader>
              <TextInput
                size="small"
                placeholder="Unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <FieldHeader>City</FieldHeader>
              <TextInput
                size="small"
                placeholder="City"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </SectionFlexRow>
          <SectionFlexRow sx={{ gap: "16px" }}>
            <FormControl sx={{ flex: 1 }}>
              <FieldHeader>State</FieldHeader>
              <Select
                size="small"
                value={state}
                onChange={(e) => setState(e.target.value)}
              >
                {STATES.map((state) => (
                  <MenuItem key={state.value} value={state.value}>
                    {state.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className="flex-1">
              <FieldHeader>Zipcode</FieldHeader>
              <TextInput
                size="small"
                placeholder="Zipcode"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
          </SectionFlexRow>
          <T variant="mdReg" sx={{ marginTop: "8px" }}>
            This is your mailing address. We need it in order to mail a card or
            important bank information.
          </T>
          <Button onClick={handleSave} disabled={!validate()}>
            Save
          </Button>
        </SectionFlexColumn>
      </ResponsePageContent>
    </Page>
  );
};

export default DebitCardChangeAddress;
